import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

// Components
import About from "./components/About";
import Contact from "./components/Contact";
import Navbar from "./components/Navbar";
import Projects from "./components/Projects";
import Skills from "./components/Skills";
import Footer from "./components/Footer";
import Blog from "./components/Blog";
import BlogPost from "./components/BlogPost";
import BlogEditor from "./components/admin/BlogEditor";
import Login from "./components/SignInForm";
// Premium Scroll Restoration
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

const App = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme ? savedTheme === "dark" : window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
    document.documentElement.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <div className={`min-h-screen font-sans transition-colors duration-500 ease-out ${
        isDarkMode ? "bg-[#09090B] text-zinc-100" : "bg-[#FAFAFA] text-zinc-900"
      }`}>
        <Navbar 
          toggleDarkMode={() => setIsDarkMode(!isDarkMode)} 
          isDarkMode={isDarkMode} 
        />

        <AnimatePresence mode="wait">
          <Routes>
            <Route
              path="/"
              element={
                <motion.main
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <About isDarkMode={isDarkMode} />
                  <Skills isDarkMode={isDarkMode} />
                  <Projects isDarkMode={isDarkMode} />
                  <Blog isDarkMode={isDarkMode} />
                  <Contact isDarkMode={isDarkMode} />
                </motion.main>
              }
            />
            
            <Route path="/blog" element={<Blog isDarkMode={isDarkMode} />} />
            <Route path="/blog/:slug" element={<BlogPost isDarkMode={isDarkMode} />} />
            <Route path="/login" element={<Login isDarkMode={isDarkMode} />} />
            
            <Route path="/blog/new" element={
              <ProtectedRoute>
                <BlogEditor isDarkMode={isDarkMode} />
              </ProtectedRoute>
            } />
            <Route path="/blog/edit/:slug" element={
              <ProtectedRoute>
                <BlogEditor isDarkMode={isDarkMode} isEdit={true} />
              </ProtectedRoute>
            } />
          </Routes>
        </AnimatePresence>

        <Footer isDarkMode={isDarkMode} />

        {/* Premium Frosted Glass Scroll-to-Top */}
        <AnimatePresence>
          {showScrollTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="fixed bottom-8 right-8 p-3.5 rounded-2xl shadow-2xl backdrop-blur-md border border-white/10 z-50 transition-all duration-300 bg-zinc-900/80 text-zinc-100 hover:bg-zinc-800 dark:bg-white/10 dark:hover:bg-white/20"
              aria-label="Scroll to top"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </Router>
  );
};

export default App;